/**
 * Utility to capture exceptions in Sentry (production only)
 *
 * Usage:
 * ```typescript
 * try {
 *   await dangerousOperation()
 * } catch (error) {
 *   console.error('Operation failed:', error)
 *   captureSentryException(error, {
 *     tags: { component: 'my-component' },
 *     extra: { userId: user.id }
 *   })
 * }
 * ```
 */

import { useFeatureFlags } from '@/composables/env/useFeatureFlags'

interface SentryContext {
  tags?: Record<string, string | number | boolean>
  extra?: Record<string, unknown>
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug'
}

export async function captureSentryException(
  error: unknown,
  context?: SentryContext,
): Promise<void> {
  // Only capture in production
  const { isPreProduction } = useFeatureFlags()
  if (!isPreProduction) return

  try {
    const Sentry = await import('@sentry/vue')
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level ?? 'error',
    })
  } catch (err) {
    // Silently fail if Sentry is not available
    console.warn('Failed to capture exception in Sentry:', err)
  }
}

export async function captureSentryMessage(
  message: string,
  context?: SentryContext,
): Promise<void> {
  // Only capture in production
  const { isPreProduction } = useFeatureFlags()
  if (!isPreProduction) return

  try {
    const Sentry = await import('@sentry/vue')
    Sentry.captureMessage(message, {
      tags: context?.tags,
      extra: context?.extra,
      level: context?.level ?? 'info',
    })
  } catch (err) {
    // Silently fail if Sentry is not available
    console.warn('Failed to capture message in Sentry:', err)
  }
}
